/**
 * useFlyCastTrace — React hook for fly cast line-tracing input
 *
 * Handles pointer events (mouse + touch unified) for tracing the bezier curve.
 * Uses rAF-throttled sampling, exponential moving average smoothing, and
 * progressive validation. Positions are tracked via refs for 60fps performance.
 *
 * State machine: IDLE → TRACING → COMPLETED (+ TIMEOUT at 8s)
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import type { FlyCurve, FlyCastResult, Point2D } from '@/types';
import { findNearestPointIndex, curveProgressAtIndex, distance } from '@/utils/flyCurve';
import { getFlyCastTier } from '@/utils/flyFishing';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';

export type FlyCastTraceState = 'idle' | 'tracing' | 'completed';

export interface UseFlyCastTraceOptions {
  curve: FlyCurve;
  containerRef: React.RefObject<HTMLElement | null>;
  onComplete: (result: FlyCastResult) => void;
  enabled: boolean;
}

export interface UseFlyCastTraceResult {
  traceState: FlyCastTraceState;
  currentAccuracy: number;
  traceProgress: number;
  tracePoints: Point2D[];
  /** Ref containing real-time trace trail for imperative rendering */
  tracePointsRef: React.MutableRefObject<Point2D[]>;
  /** Current accuracy scores per sample (for color feedback) */
  lastSampleScore: number;
}

// Smoothing factor for exponential moving average (0-1, higher = more responsive)
const SMOOTH_FACTOR = 0.3;
// Minimum pointer movement to register a sample (pixels)
const MIN_MOVEMENT_PX = 3;
// How far forward (as fraction) the player can skip before it's considered cheating
const MAX_SKIP_FRACTION = 0.15;
// Completion threshold: how close to end point to auto-complete (pixels)
const END_POINT_TOLERANCE = 30;
// Completion threshold: minimum trace progress to allow completion
const MIN_COMPLETION_PROGRESS = 0.75;

export function useFlyCastTrace(options: UseFlyCastTraceOptions): UseFlyCastTraceResult {
  const { curve, containerRef, onComplete, enabled } = options;

  // State exposed to React for UI updates
  const [traceState, setTraceState] = useState<FlyCastTraceState>('idle');
  const [currentAccuracy, setCurrentAccuracy] = useState(0);
  const [traceProgress, setTraceProgress] = useState(0);
  const [tracePoints, setTracePoints] = useState<Point2D[]>([]);
  const [lastSampleScore, setLastSampleScore] = useState(0);

  // Refs for high-frequency data (not React state to avoid re-renders)
  const tracePointsRef = useRef<Point2D[]>([]);
  const smoothedPosRef = useRef<Point2D | null>(null);
  const lastSampleTimeRef = useRef<number>(0);
  const traceStartTimeRef = useRef<number>(0);
  const currentProgressIndexRef = useRef<number>(0);
  const sampleScoresRef = useRef<number[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<FlyCastTraceState>('idle');
  const rafIdRef = useRef<number | null>(null);
  const uiUpdateCounterRef = useRef(0);

  // Stable callback refs
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const curveRef = useRef(curve);
  curveRef.current = curve;

  /**
   * Get pointer position relative to the SVG container
   */
  const getRelativePosition = useCallback(
    (clientX: number, clientY: number): Point2D | null => {
      const container = containerRef.current;
      if (!container) return null;

      const svg = container.querySelector('svg');
      if (!svg) return null;

      const rect = svg.getBoundingClientRect();

      // Map client coordinates to SVG viewBox coordinates.
      // The viewBox may have a non-zero origin (offsetX, offsetY) which
      // shifts all SVG coordinates. We must add this offset after scaling.
      const viewBox = svg.viewBox?.baseVal;
      const svgWidth = viewBox?.width || rect.width;
      const svgHeight = viewBox?.height || rect.height;
      const viewBoxX = viewBox?.x || 0;
      const viewBoxY = viewBox?.y || 0;

      const scaleX = svgWidth / rect.width;
      const scaleY = svgHeight / rect.height;

      return {
        x: (clientX - rect.left) * scaleX + viewBoxX,
        y: (clientY - rect.top) * scaleY + viewBoxY,
      };
    },
    [containerRef],
  );

  /**
   * Complete the trace and report results
   */
  const completeTrace = useCallback(
    (reason: 'finished' | 'timeout' | 'released') => {
      if (stateRef.current !== 'tracing') return;

      stateRef.current = 'completed';
      setTraceState('completed');

      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const scores = sampleScoresRef.current;
      const avgAccuracy =
        scores.length > 0 ? scores.reduce((sum, s) => sum + s, 0) / scores.length : 0;

      const progressIdx = currentProgressIndexRef.current;
      const completionPercent = curveProgressAtIndex(curveRef.current, progressIdx);

      // If completed by timeout or release with low progress, accuracy is penalized
      let finalAccuracy = avgAccuracy;
      if (reason !== 'finished' && completionPercent < MIN_COMPLETION_PROGRESS) {
        finalAccuracy *= completionPercent; // Scale down by how much was traced
      }

      const durationMs = performance.now() - traceStartTimeRef.current;

      const result: FlyCastResult = {
        accuracy: finalAccuracy,
        tier: getFlyCastTier(finalAccuracy),
        completionPercent,
        tracePoints: [...tracePointsRef.current],
        durationMs,
      };

      setCurrentAccuracy(finalAccuracy);
      setTraceProgress(completionPercent);

      onCompleteRef.current(result);
    },
    [],
  );

  /**
   * Process a single pointer sample during tracing
   */
  const processSample = useCallback(
    (rawPos: Point2D) => {
      if (stateRef.current !== 'tracing') return;

      const now = performance.now();
      // rAF throttle: max ~60fps sampling
      if (now - lastSampleTimeRef.current < 16) return;
      lastSampleTimeRef.current = now;

      // Exponential moving average smoothing
      const smoothed = smoothedPosRef.current;
      let pos: Point2D;
      if (smoothed) {
        pos = {
          x: smoothed.x + SMOOTH_FACTOR * (rawPos.x - smoothed.x),
          y: smoothed.y + SMOOTH_FACTOR * (rawPos.y - smoothed.y),
        };
      } else {
        pos = rawPos;
      }
      smoothedPosRef.current = pos;

      // Minimum movement threshold
      const points = tracePointsRef.current;
      if (points.length > 0) {
        const lastPoint = points[points.length - 1];
        if (distance(pos, lastPoint) < MIN_MOVEMENT_PX) return;
      }

      // Find nearest curve point from current progress forward
      const crv = curveRef.current;
      const nearestIdx = findNearestPointIndex(crv, pos, currentProgressIndexRef.current);

      // Progressive validation: can't skip too far ahead
      const currentProgress = curveProgressAtIndex(crv, currentProgressIndexRef.current);
      const nearestProgress = curveProgressAtIndex(crv, nearestIdx);
      const progressJump = nearestProgress - currentProgress;

      if (progressJump > MAX_SKIP_FRACTION) {
        // Player tried to skip ahead — use current progress position instead
        // Still score the sample but don't advance progress
      } else if (nearestIdx >= currentProgressIndexRef.current) {
        // Valid forward progress
        currentProgressIndexRef.current = nearestIdx;
      }

      // Score this sample based on distance to curve
      const dist = distance(pos, crv.points[nearestIdx]);
      const maxTolerance = BALANCE_CONFIG.FLY_CAST_GOOD_TOLERANCE;
      const sampleScore = Math.max(0, 1 - dist / maxTolerance);
      sampleScoresRef.current.push(sampleScore);

      // Store trace point
      tracePointsRef.current.push(pos);

      // Update last sample score for color feedback
      setLastSampleScore(sampleScore);

      // Batch React state updates (every 4th sample to avoid excessive re-renders)
      uiUpdateCounterRef.current++;
      if (uiUpdateCounterRef.current % 4 === 0) {
        const scores = sampleScoresRef.current;
        const avgAcc =
          scores.length > 0 ? scores.reduce((sum, s) => sum + s, 0) / scores.length : 0;
        setCurrentAccuracy(avgAcc);
        setTraceProgress(curveProgressAtIndex(crv, currentProgressIndexRef.current));
        setTracePoints([...tracePointsRef.current]);
      }

      // Check if trace is complete (near end point)
      const endDist = distance(pos, crv.end);
      const endProgress = curveProgressAtIndex(crv, currentProgressIndexRef.current);
      if (endDist < END_POINT_TOLERANCE && endProgress >= MIN_COMPLETION_PROGRESS) {
        completeTrace('finished');
      }
    },
    [completeTrace],
  );

  /**
   * Handle pointer down — start tracing if near start point
   */
  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (stateRef.current !== 'idle') return;

      const pos = getRelativePosition(e.clientX, e.clientY);
      if (!pos) return;

      const crv = curveRef.current;
      const startDist = distance(pos, crv.start);

      if (startDist > BALANCE_CONFIG.FLY_CAST_START_TOLERANCE) return;

      // Begin tracing
      stateRef.current = 'tracing';
      setTraceState('tracing');
      traceStartTimeRef.current = performance.now();
      currentProgressIndexRef.current = 0;
      sampleScoresRef.current = [];
      tracePointsRef.current = [pos];
      smoothedPosRef.current = pos;
      lastSampleTimeRef.current = performance.now();
      uiUpdateCounterRef.current = 0;

      setTracePoints([pos]);
      setCurrentAccuracy(0);
      setTraceProgress(0);

      // Start timeout
      timeoutRef.current = setTimeout(() => {
        completeTrace('timeout');
      }, BALANCE_CONFIG.FLY_CAST_TIMEOUT_MS);

      // Capture pointer for reliable tracking outside the element
      (e.target as Element)?.setPointerCapture?.(e.pointerId);
    },
    [getRelativePosition, completeTrace],
  );

  /**
   * Handle pointer move — process trace samples
   */
  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (stateRef.current !== 'tracing') return;

      const pos = getRelativePosition(e.clientX, e.clientY);
      if (!pos) return;

      processSample(pos);
    },
    [getRelativePosition, processSample],
  );

  /**
   * Handle pointer up — finish trace if released
   */
  const handlePointerUp = useCallback(() => {
    if (stateRef.current !== 'tracing') return;
    completeTrace('released');
  }, [completeTrace]);

  // Attach/detach pointer event listeners
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerUp);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [enabled, handlePointerDown, handlePointerMove, handlePointerUp, containerRef]);

  // Reset state when curve changes or when re-enabled
  useEffect(() => {
    if (!enabled) return;

    stateRef.current = 'idle';
    setTraceState('idle');
    setCurrentAccuracy(0);
    setTraceProgress(0);
    setTracePoints([]);
    setLastSampleScore(0);
    tracePointsRef.current = [];
    smoothedPosRef.current = null;
    currentProgressIndexRef.current = 0;
    sampleScoresRef.current = [];
    uiUpdateCounterRef.current = 0;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [curve, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  return {
    traceState,
    currentAccuracy,
    traceProgress,
    tracePoints,
    tracePointsRef,
    lastSampleScore,
  };
}
